import { groupBy } from 'lodash';
import styled from 'styled-components';
import { useGraphql } from 'utils/hooks/useGraphql';
import noop from 'utils/noop';

import {
  fetchChannelTypesQuery,
  fetchLocalesQuery,
  fetchPlacementsQuery,
  fetchTemplatesMetaQuery,
} from 'api/queries';
import { EmbeddedMessage, WhatsApp } from 'constants/messageMediums';
import { WhatsAppSenderStatus } from 'modules/graphql';
import {
  MetaApprovalStatusDefinition,
  MetaQualityRating,
  PseudoApprovalStatusDefinition,
  TemplateTypeDefinition,
} from 'modules/graphql/types/GraphQLTemplate';
import {
  FILTER_PREFIXES,
  META_APPROVAL_STATUS_FILTER_VISIBILITY,
  TEMPLATE_INDEX_FILTERS,
  WHATSAPP_QUALITY_RATING_FILTER_ORDER,
  WHATSAPP_STATUS_FILTER_ORDER_GA,
} from 'modules/templateIndex/constants';
import {
  BasicOption,
  ExpandableFilterMap,
  FilterCategory,
  GroupedOption,
} from 'modules/templateIndex/types';
import {
  getApprovalStatusLabel,
  getLocaleLabel,
  getQualityRatingLabel,
  getTemplateTypeLabel,
  getTotalSelectionsByFilterGroup,
} from 'modules/templateIndex/utils';
import { useMemo } from 'react';
import { Help24 } from 'ui/icons/aurora/24px';
import { FilterMenuTriggerButton } from 'ui/indexPages/filters/FilterMenuTriggerButton';
import { Menu, MenuItemOption } from 'ui/menu';
import { Tooltip } from 'ui/tooltip';
import {
  useEmbeddedMessagingCoreFeature,
  useWhatsAppMessageChannelFeature,
} from 'utils/hooks/featureFlags';

const ALL_MEDIUMS = 'All mediums';

const CREATOR_CATEGORY: FilterCategory = {
  hasFilter: true,
  id: 'creator',
  isSelectMenu: true,
  label: 'Created by',
  options: [],
  forMedium: ALL_MEDIUMS,
};

const MODIFIER_CATEGORY: FilterCategory = {
  hasFilter: true,
  id: 'modifier',
  isSelectMenu: true,
  label: 'Modified by',
  options: [],
  forMedium: ALL_MEDIUMS,
};

const LOCALE_CATEGORY: FilterCategory = {
  hasFilter: true,
  id: 'locales',
  isSelectMenu: true,
  label: 'Locales',
  options: [],
  forMedium: ALL_MEDIUMS,
};

const MESSAGE_TYPE_CATEGORY: FilterCategory = {
  id: 'channel-type',
  isSelectMenu: true,
  label: 'Channel type',
  options: [
    { label: 'Marketing', id: 'marketing' },
    { label: 'Transactional', id: 'transactional' },
  ],
  forMedium: ALL_MEDIUMS,
};

const PLACEMENTS_CATEGORY: FilterCategory = {
  id: 'placements',
  hasFilter: true,
  isSelectMenu: true,
  label: 'Placements',
  options: [],
  forMedium: EmbeddedMessage,
};

const WHATSAPP_TYPE_CATEGORY: FilterCategory = {
  id: 'whatsapp-type',
  isSelectMenu: true,
  label: 'Type',
  options: Object.values(TemplateTypeDefinition).map((type) => ({
    label: getTemplateTypeLabel(type),
    id: `${FILTER_PREFIXES.WHATSAPP_TYPE}${type}`,
  })),
  forMedium: WhatsApp,
};

const WHATSAPP_TEMPLATE_QUALITY_RATING_OPTIONS = {
  isExpandableGroup: true,
  options: Object.values(MetaQualityRating)
    .sort(
      (a, b) =>
        WHATSAPP_QUALITY_RATING_FILTER_ORDER.indexOf(a) -
        WHATSAPP_QUALITY_RATING_FILTER_ORDER.indexOf(b),
    )
    .map((qualityRating) => ({
      label: getQualityRatingLabel(qualityRating),
      id: `${FILTER_PREFIXES.QUALITY_RATING}${qualityRating}`,
    })),
};

const WHATSAPP_STATUS_CATEGORY = (isWhatsAppGAEnabled: boolean): FilterCategory => ({
  id: 'status',
  isSelectMenu: true,
  label: 'Status',
  options: Object.values({ ...MetaApprovalStatusDefinition, ...PseudoApprovalStatusDefinition })
    .filter((s) => META_APPROVAL_STATUS_FILTER_VISIBILITY(isWhatsAppGAEnabled)[s])
    .sort((a, b) =>
      isWhatsAppGAEnabled
        ? WHATSAPP_STATUS_FILTER_ORDER_GA.indexOf(a) - WHATSAPP_STATUS_FILTER_ORDER_GA.indexOf(b)
        : 0,
    )
    .map(
      (status): GroupedOption => ({
        label: getApprovalStatusLabel(status, isWhatsAppGAEnabled),
        id: `${FILTER_PREFIXES.STATUS}${status}`,
        ...(isWhatsAppGAEnabled &&
          status === MetaApprovalStatusDefinition.Approved &&
          WHATSAPP_TEMPLATE_QUALITY_RATING_OPTIONS),
      }),
    ),
  forMedium: WhatsApp,
});

const HelpTooltip = ({ tooltipContent }: { tooltipContent: string }) => (
  <Tooltip tooltipContent={tooltipContent}>
    <Help24 />
  </Tooltip>
);

const WHATSAPP_SENDER_STATUS_OPTIONS = [
  {
    label: 'Sending limit reached',
    id: `${FILTER_PREFIXES.WHATSAPP_SENDER_STATUS}${WhatsAppSenderStatus.Restricted}`,
    suffix: (
      <HelpTooltip tooltipContent="Senders restricted for reaching the 24-hour limit. New conversations skipped, but replies allowed." />
    ),
  },
  {
    label: 'Flagged',
    id: `${FILTER_PREFIXES.WHATSAPP_SENDER_STATUS}${WhatsAppSenderStatus.Flagged}`,
    suffix: (
      <HelpTooltip tooltipContent="Senders flagged for low quality. Limit upgrades blocked; may decrease in 7 days if not improved." />
    ),
  },
];

/** Note: Usage of sender quality rating filters was rescinded after BE+FE support was already implemented.
 * Opting to leave implementation in place, making it easy to add sender quality rating filters in the
 * future as needed by adding to the array below. Use id values aligned to prefix and quality rating enums:
 * `${FILTER_PREFIXES.WHATSAPP_SENDER_QUALITY_RATING}${MetaQualityRating.*}`. As is, filters will render
 * under the "Sender" filter category's nested menu options. */
const WHATSAPP_SENDER_QUALITY_RATING_OPTIONS = [] as BasicOption[];

const WHATSAPP_SENDER_CATEGORY: FilterCategory = {
  id: 'sender',
  isSelectMenu: true,
  label: 'Sender',
  options: [...WHATSAPP_SENDER_STATUS_OPTIONS, ...WHATSAPP_SENDER_QUALITY_RATING_OPTIONS],
  forMedium: WhatsApp,
};

// the menu filters don't use the chevron icon hardcoded in the menu
const NoChevronMenu = styled.div`
  [data-test='menu-chevron'] {
    display: none;
  }
`;

// TODO: [PRO-3634] `TEMPLATE_FILTER_DISPLAY_CONTENT` convention for localization. Turn text to translate into a localization en-US.json
export const TEMPLATE_FILTER_DISPLAY_CONTENT = {
  title: 'Filters',
};

type TemplateIndexFilterProps = {
  savedSelections: string[];
  onSelect: (a: TEMPLATE_INDEX_FILTERS) => void;
};

export const TemplateIndexFilter = ({
  savedSelections = [],
  onSelect = noop,
}: TemplateIndexFilterProps) => {
  const isEmbeddedMessagingEnabled = useEmbeddedMessagingCoreFeature();
  const { isWhatsAppEnabled, isWhatsAppGAEnabled } = useWhatsAppMessageChannelFeature();

  const { data = {} } = useGraphql(fetchTemplatesMetaQuery, { notifyOnNetworkStatusChange: true });
  const { templateCreators = [], templateModifiers = [] } = data;

  const { data: localeData = {} } = useGraphql(fetchLocalesQuery);
  const locales = localeData?.locales || [];

  const { data: channelTypesEnum } = useGraphql(fetchChannelTypesQuery);
  const channelTypes = channelTypesEnum?.channelTypes || [];

  const { data: placementsData } = useGraphql(fetchPlacementsQuery);
  const placements = placementsData?.placements ?? [];

  /**
   * The menu component assumes a unique ID on each option. The backend shares ids for certain values, so we need to disambiguate them in order to track selections without toggling values on multiple options with a click
   * Prefixing each value in the options list solves that, but we'll have to strip the prefix value
   */
  const builtFilterCategories: FilterCategory[] = useMemo(
    () => [
      {
        ...CREATOR_CATEGORY,
        options: templateCreators.map((creator: any) => ({
          label: creator.fullName,
          id: `${FILTER_PREFIXES.CREATOR}${creator.id}`,
        })),
      },
      {
        ...MODIFIER_CATEGORY,
        options: templateModifiers.map((modifier: any) => ({
          label: modifier.fullName,
          id: `${FILTER_PREFIXES.MODIFIER}${modifier.id}`,
        })),
      },
      {
        ...LOCALE_CATEGORY,
        options: locales
          .map((locale: any) => ({
            label: getLocaleLabel(locale),
            id: `${FILTER_PREFIXES.LOCALE}${locale.tag}`,
          }))
          .sort((a: any, b: any) => a.label.localeCompare(b.label)),
      },
      {
        ...MESSAGE_TYPE_CATEGORY,
        options: channelTypes.map((msgType: any) => ({
          label: msgType,
          id: `${FILTER_PREFIXES.CHANNEL_TYPE}${msgType}`,
        })),
      },
      {
        ...PLACEMENTS_CATEGORY,
        options: placements.map(
          (placement: { payload: { id: number; fields: { placementName: string } } }) => ({
            id: `${FILTER_PREFIXES.PLACEMENTS}${placement?.payload?.id}`,
            label: placement?.payload?.fields?.placementName,
          }),
        ),
      },
      WHATSAPP_STATUS_CATEGORY(isWhatsAppGAEnabled),
      WHATSAPP_TYPE_CATEGORY,
      ...(isWhatsAppGAEnabled ? [WHATSAPP_SENDER_CATEGORY] : []),
    ],
    [templateCreators, templateModifiers, locales, channelTypes, placements, isWhatsAppGAEnabled],
  );

  /** A map of the parents and children that exist in an expandable filter category. */
  const expandableFilterMap = useMemo(
    () =>
      builtFilterCategories
        .flatMap((filterCategory) => filterCategory.options)
        .reduce((acc: ExpandableFilterMap, parent) => {
          if ('isExpandableGroup' in parent && parent.isExpandableGroup) {
            const children = parent.options ?? [];
            let nestedMappings: ExpandableFilterMap = { ...acc, [parent.id]: { children } };
            children.forEach((child) => (nestedMappings[child.id] = { parentId: parent.id }));
            return nestedMappings;
          }
          return acc;
        }, {}),
    [builtFilterCategories],
  );

  /**
    We're going to need to be able to disambiguate the selected filters to build the proper GraphQL query, so this is a structure which associates selected IDs with the associated filter. It uses `builtFilterOptions` because we need the prefixed ids
   */
  const [
    creatorIdMap,
    modifierIdMap,
    localesIdMap,
    messageTypeIdMap,
    placementIdsMap,
    statusIdMap,
    whatsappTypeIdMap,
    qualityRatingIdMap,
    whatsAppSenderStatusIdMap,
    whatsAppSenderQualityRatingIdMap,
  ] = [
    builtFilterCategories[0].options.map((creator) => creator.id),
    builtFilterCategories[1].options.map((modifier) => modifier.id),
    builtFilterCategories[2].options.map((locale) => locale.id),
    builtFilterCategories[3].options.map((msgType) => msgType.id),
    builtFilterCategories[4].options.map((placement) => placement.id),
    builtFilterCategories[5].options.map((status) => status.id),
    builtFilterCategories[6].options.map((whatsappType) => whatsappType.id),
    WHATSAPP_TEMPLATE_QUALITY_RATING_OPTIONS.options.map((qualityRating) => qualityRating.id),
    WHATSAPP_SENDER_STATUS_OPTIONS.map((status) => status.id),
    WHATSAPP_SENDER_QUALITY_RATING_OPTIONS.map((qualityRating) => qualityRating.id),
  ];

  /** Keys must match values of FilterCategory id in order to render badge counts per category. */
  const filterTotals: Record<FilterCategory['id'], number> = {
    creator: getTotalSelectionsByFilterGroup(creatorIdMap, savedSelections),
    modifier: getTotalSelectionsByFilterGroup(modifierIdMap, savedSelections),
    locales: getTotalSelectionsByFilterGroup(localesIdMap, savedSelections),
    'channel-type': getTotalSelectionsByFilterGroup(messageTypeIdMap, savedSelections),
    placements: getTotalSelectionsByFilterGroup(placementIdsMap, savedSelections),
    /** Quality rating filters are grouped distinctly for graphql, but they are rendered within the "status" category. */
    status: getTotalSelectionsByFilterGroup(
      [...statusIdMap, ...qualityRatingIdMap],
      savedSelections,
    ),
    'whatsapp-type': getTotalSelectionsByFilterGroup(whatsappTypeIdMap, savedSelections),
    sender: getTotalSelectionsByFilterGroup(whatsAppSenderStatusIdMap, savedSelections),
  };

  const allFiltersTotal = Object.keys(filterTotals).reduce(
    (total, filterCategory) => total + filterTotals[filterCategory as keyof typeof filterTotals],
    0,
  );

  const builtFilterCategoriesWithTotals: MenuItemOption[] = builtFilterCategories
    .map((option) => ({ ...option, hasCountBadge: filterTotals[option.id] > 0 }))
    .filter((option) => option.id !== LOCALE_CATEGORY.id || locales.length > 1) // filter only makes sense with 2+ options
    .filter((option) => isWhatsAppEnabled || option?.forMedium !== WhatsApp)
    .filter((option) => isEmbeddedMessagingEnabled || option?.forMedium !== EmbeddedMessage);

  const handleSelection = (selectedId: string) => {
    /** selectionSet is a list of selected string id values; a set makes duplicate-checking easy. */
    const selectionSet = new Set<string>(savedSelections);

    const { parentId, children } = expandableFilterMap[selectedId] ?? {};

    if (children) {
      // if selecting parent group, select all children; if deselecting parent group, deselect all children
      if (!selectionSet.has(selectedId)) {
        selectionSet.add(selectedId);
        children.forEach((child) => selectionSet.add(child.id));
      } else {
        selectionSet.delete(selectedId);
        children.forEach((child) => selectionSet.delete(child.id));
      }
    } else if (parentId) {
      // if all children are selected, select parent group; if all children are deselected, deselect parent group
      const { children } = expandableFilterMap[parentId];
      if (!selectionSet.has(selectedId)) {
        selectionSet.add(selectedId);
        const allChildrenSelected = children?.every((child) => selectionSet.has(child.id));
        if (allChildrenSelected) selectionSet.add(parentId);
      } else {
        selectionSet.delete(selectedId);
        const allChildrenDeselected = children?.every((child) => !selectionSet.has(child.id));
        if (allChildrenDeselected) selectionSet.delete(parentId);
        // remove parent even if one of the children is removed
        else if (selectionSet.has(parentId)) selectionSet.delete(parentId);
      }
    } else {
      if (selectionSet.has(selectedId)) selectionSet.delete(selectedId);
      else selectionSet.add(selectedId);
    }

    // throw it back to an array so we can filter it down for each filter type
    const selections = [...selectionSet];

    // return a keyed structure to the call site so we can build the graphql query
    // these MUST batch the keys from Type BaseFilters
    onSelect({
      creators: selections.filter((selection) => creatorIdMap.includes(selection)),
      modifiers: selections.filter((selection) => modifierIdMap.includes(selection)),
      locales: selections.filter((selection) => localesIdMap.includes(selection)),
      channelTypes: selections.filter((selection) => messageTypeIdMap.includes(selection)),
      placements: selections.filter((selection) => placementIdsMap.includes(selection)),
      statuses: selections.filter((selection) => statusIdMap.includes(selection)),
      whatsappTypes: selections.filter((selection) => whatsappTypeIdMap.includes(selection)),
      qualityRatings: selections.filter((selection) => qualityRatingIdMap.includes(selection)),
      whatsAppSenderStatuses: selections.filter((selection) =>
        whatsAppSenderStatusIdMap.includes(selection),
      ),
      whatsAppSenderQualityRatings: selections.filter((selection) =>
        whatsAppSenderQualityRatingIdMap.includes(selection),
      ),
    });
  };

  const groupedByMedium = groupBy(builtFilterCategoriesWithTotals, 'forMedium');
  const builtFilterCategoriesWithTotalsAndGroups =
    Object.keys(groupedByMedium)?.length > 1
      ? Object.keys(groupedByMedium).reduce(
          (builtOptions: any, forMedium, index) => [
            ...builtOptions,
            ...(index !== 0 ? [{ isDivider: true, id: `medium-divider-${index}`, label: '' }] : []),
            { isDisabled: true, isTitle: true, id: `medium-label-${index}`, label: forMedium },
            ...groupedByMedium[forMedium],
          ],
          [],
        )
      : builtFilterCategoriesWithTotals;

  return (
    <NoChevronMenu>
      <Menu
        data-test="template-index-filter"
        defaultSelectedOptionIds={savedSelections}
        handleSelectionChange={handleSelection}
        options={builtFilterCategoriesWithTotalsAndGroups}
      >
        <FilterMenuTriggerButton selectedFilterCount={allFiltersTotal} />
      </Menu>
    </NoChevronMenu>
  );
};
